// Discovery Tax case study — content kept verbatim from source.
export const discoveryTax = {
  slug: 'discovery-tax',
  eyebrow: 'C&I Solar + Storage',
  series: 'Regulatory Intelligence Brief · No. 01',
  date: '2026-06-01',
  readTime: 6,
  title: 'The Discovery Tax: $16,500 a month',
  titleHighlight: '$16,500',
  dek: `Hardware costs collapsed. The regulatory work didn't, and it's now one of the most stubborn soft costs in a commercial solar project. This is an anatomy of where that time goes and what it's worth.`,
  dekBold: 'most stubborn soft costs',
  metaDescription:
    "Hardware costs collapsed. Regulatory work didn't. Inside the Discovery Tax — and the $16,500/month it costs a single 1 MW C&I solar project stuck in permitting.",
  seoKeywords: [
    'C&I solar',
    'commercial solar',
    'permitting',
    'interconnection',
    'soft costs',
    'NFPA 855',
    'jurisdictional requirements',
    'solar development',
  ],
  heroMeta: {
    byline: 'Fordje Research',
    date: 'June 2026',
    readTime: '6 min read',
    methodology: '9 practitioner interviews + peer-reviewed data',
  },
  tldr: {
    title: 'AT A GLANCE',
    points: [
      'Soft costs are now <b>50.1% of a commercial solar project,</b>&nbsp;and the regulatory slice is one that hasn\'t fallen.',
      'A 1 MW project stuck in review burns <b>$16,500/month</b> in forgone energy value and another <b>$340–425/day</b> in carrying cost.',
      'The fix isn\'t faster reviewers — it\'s complete, current requirements on day one across <b>~28,000 jurisdictions</b>.',
    ],
  },
  lede: [
    {
      html: `A commercial solar project takes 18 to 36 months to develop.<sup data-fn="1">1</sup> The panels go up in weeks; most of the rest is process. And that process now runs to <span data-stat>50.1% of system cost</span><sup data-fn="2">2</sup>&nbsp;and the\n\nregulatory work is the slice that hasn't come down. With commercial system prices now <em>rising</em> on tariff-driven equipment costs,<sup data-fn="3">3</sup> process is the cost lever developers still control.`,
    },
    {
      html: `The slice that won't fall is regulatory: requirements that vary by jurisdiction and aren't written down clearly. Agency processing times have stabilized in recent years,&nbsp;helped by\n\npermit software&nbsp;streamlining.4&nbsp;What remains tricky is the&nbsp;variation.&nbsp;Requirements differ as much from one jurisdiction to the next as the timelines do, and across roughly <span data-stat>28,000 jurisdictions and 3,000 utilities</span>, that variability is what drives compliance cost and time. Reviews routinely add weeks or months per project,<sup data-fn="5">5</sup> and peer-reviewed analysis of <span data-stat>248,741 installations</span> found that local requirement variations directly lengthen project durations.<sup data-fn="6">6</sup> Storage compounds it: jurisdictions enforce different editions of NFPA&nbsp;855, so the same battery design can pass in one county and fail next door.<sup data-fn="7">7</sup>`,
    },
  ],
  anatomyIntro: `SEIA and Wood Mackenzie name the same issues, and note that developers navigate them largely through regional expertise and trusted relationships.<sup data-fn="8">8</sup> In our interviews with C&I and community-scale developers and their consultants, that reliance showed up as the same four phases on every project:<sup data-fn="9">9</sup>`,
  phases: [
    {
      n: '01',
      title: 'Discovery',
      stat: '~40 hrs',
      body: '~40 hrs of due diligence per project before submission. The hardest hours go to confirming a jurisdiction has no clear pathway at all.',
    },
    {
      n: '02',
      title: 'Submission',
      stat: '~1 in 10',
      body: 'First-pass permit approval as low as ~1 in 10 in tougher markets. Interconnection: 2–3 edit rounds, a fee and weeks per round.',
    },
    {
      n: '03',
      title: 'Rework',
      stat: '25–40%',
      body: '25–40% of projects return for redesign — an overlay district, a comp-plan clause, a county rule layered above a compliant use.',
    },
    {
      n: '04',
      title: 'Change',
      stat: '7 mo',
      body: 'Requirements move while projects sit. One setback shifted to 500 ft over seven months. The project was dead, but they didn’t know until months later.',
    },
  ],
  heroQuote: null,
  quotes: [
    { quote: `The hardest part is proving a negative — that there's no pathway at all.`, cite: 'Development consultant' },
    { quote: `The number of times we update drawings on the fly — and that is risky.`, cite: 'VP, C&I developer' },
  ],
  numberBlock: {
    headline: '$16,500 / month · $540 / day',
    since: 'forgone energy value, one 1 MW project',
    paragraphs: [
      `An example 1 MW C&I project in Atlanta sells ~1.42 million kWh a year.<sup data-fn="10">10</sup> Every month it's stuck in permitting or interconnection instead of producing, that's $16,500 gone, $540 a day at the U.S. average commercial rate.<sup data-fn="11">11</sup> That's one project, one month; across a portfolio it adds up fast.`,
      `And forgone revenue is only half of it. Once a developer has sunk capital into a project — land, engineering, equipment, deposits — that money carries a cost whether the project is producing or stuck. On a roughly $1.55 million system, the carrying cost alone runs <strong>$340 to $425 a day</strong>, every day it sits in review.<sup data-fn="12">12</sup> Delay doesn't just postpone the upside; it bleeds the balance sheet while you wait.`,
      `That's the visible cost. The invisible one is worse: the project that dies after you've spent two years and real money getting it to the finish line.`,
    ],
  },
  boundary: {
    kicker: 'Process, not people',
    html: `Complete, current requirements don't change a reviewer's discretion, a hearing schedule, or a utility's study queue. In some territories,&nbsp;interconnection backlogs take years..<sup data-fn="13">13</sup> That's the people element: the judgment and relationships your team is right to own, and that no dataset should replace.<br /><br />What complete requirements <strong>do</strong> remove is everything upstream: the discovery hours, the resubmissions from wrong or outdated requirements, the redesigns triggered by provisions nobody surfaced, and the projects quietly killed by a code change nobody was tracking. That's the Discovery Tax, and unlike the queue, it's optional.`,
  },
  close: {
    eyebrow: 'What Fordje does',
    headline: 'The requirements intelligence underneath the permit.',
    body: `Fordje structures the full regulatory picture for a project — the codes, the checklists, the fee schedules, the buried requirements that decide a permit — laddered to the right city, county, state, and utility, and kept current as codes change. Your team has it correct on day one, so you avoid the bottlenecks at permitting and interconnection instead of discovering them there.`,
    proofPoints: [
      { stat: '~28,000', label: 'U.S. jurisdictions covered' },
      { stat: 'Codes + amendments + utility rules', label: 'in one structured view' },
      { stat: 'Continuously', label: 'refreshed and alerts sent as requirements change' },
    ],
    ctaLabel: 'Book a Demo',
    ctaHref: '/request-demo',
    secondaryCtaLabel: 'Try it free',
    secondaryCtaHref: '/try-it',
  },
  sources: [
    `<b>SEIA / Wood Mackenzie</b>, U.S. Solar Market Insight, Q2 2024 (18–36 mo commercial development timelines).`,
    `<b>NREL/TP-7A40-92536</b> (Jan 2025), Fig. 4: commercial soft cost 50.1% of total installed cost in 2024, near half since 2014.`,
    `<b>SEIA / Wood Mackenzie</b>, SMI Q2 2026: commercial system pricing +4% YoY to $1.67/Wdc; BOS equipment +60% YoY.`,
    `<b>O'Shaughnessy, Barbose & Wiser</b>, "Patience is a virtue," <i>Energy Policy</i> (2020): durations stabilized after streamlining; variation within jurisdictions is as large as across them. Residential data.`,
    `<b>NREL</b>, Solar PII Timelines: jurisdictional reviews routinely add weeks or months per project.`,
    `<b>O'Shaughnessy et al.</b>, "Effects of local permitting and interconnection requirements on solar PV installation durations," <i>Energy Policy</i> 161 (2022): 248,741 systems.`,
    `<b>Energy-Storage.News</b> (2026): jurisdictions enforce differing NFPA 855 editions; early AHJ coordination required where adoption lags.`,
    `<b>SEIA / Wood Mackenzie</b>, SMI Q2 2026: permitting headwind through 2031; interconnection delays reduce 2026 commercial installs in NY & MA.`,
    `<b>Interviews</b> conducted by Fordje with C&I and community-scale developers, corporate solar buyers, and consultants, May–June 2026. Practitioner estimates, not audited data.`,
    `<b>NREL PVWatts</b>: 1,000-kWdc fixed open-rack, Atlanta GA = 1,424,945 kWh/yr. Varies by location and design.`,
    `<b>U.S. EIA</b>, Electric Power Monthly, Table 5.6.A: U.S. commercial avg 13.92¢/kWh (Mar 2026), up from 13.16¢ YoY; GA avg 11.57¢.`,
    `<b>NREL/TP-7A40-92536</b>, Table A-1: 2024 commercial benchmark $1.55/Wdc (3-MWdc reference). Carrying cost assumes 8–10% cost of capital (stated assumption).`,
    `<b>IREC</b>, Improving Interconnection Timelines: documented DER backlogs, incl. 300+ projects queued at a single utility.`,
  ],
};

export type CaseStudy = typeof discoveryTax;
